import { useState } from 'react';
import type { TagTreeNode, HierarchicalTagProfile } from '@mtg-deck-tool/shared';
import { useSearchStore } from '../../stores/search-store.js';

interface TagTreeProps {
  hierarchy: HierarchicalTagProfile;
}

export function TagTree({ hierarchy }: TagTreeProps) {
  const toggleTagFilter = useSearchStore((s) => s.toggleTagFilter);
  const clearSearch = useSearchStore((s) => s.clearSearch);
  const activeTagSlugs = useSearchStore((s) => s.activeTagSlugs);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  // Filter out tags that only appear once
  const filteredRoots = hierarchy.roots.filter((n) => n.totalCount > 1);

  // Split roots into two columns
  const mid = Math.ceil(filteredRoots.length / 2);
  const col1 = filteredRoots.slice(0, mid);
  const col2 = filteredRoots.slice(mid);

  return (
    <div>
      <div className="grid grid-cols-2 gap-x-1">
        <div className="space-y-0.5">
          {col1.map((node) => (
            <TagTreeBranch
              key={node.slug}
              node={node}
              depth={0}
              expanded={expanded}
              toggleExpand={toggleExpand}
              activeTagSlugs={activeTagSlugs}
              toggleTagFilter={toggleTagFilter}
              clearSearch={clearSearch}
            />
          ))}
        </div>
        <div className="space-y-0.5">
          {col2.map((node) => (
            <TagTreeBranch
              key={node.slug}
              node={node}
              depth={0}
              expanded={expanded}
              toggleExpand={toggleExpand}
              activeTagSlugs={activeTagSlugs}
              toggleTagFilter={toggleTagFilter}
              clearSearch={clearSearch}
            />
          ))}
        </div>
      </div>
      <div className="pt-1 text-[10px] text-gray-600">
        {hierarchy.taggedCards} tagged · {hierarchy.untaggedCards} with no tag data
      </div>
    </div>
  );
}

interface TagTreeBranchProps {
  node: TagTreeNode;
  depth: number;
  expanded: Set<string>;
  toggleExpand: (slug: string) => void;
  activeTagSlugs: string[];
  toggleTagFilter: (slug: string, multi: boolean) => void;
  clearSearch: () => void;
}

function TagTreeBranch({ node, depth, expanded, toggleExpand, activeTagSlugs, toggleTagFilter, clearSearch }: TagTreeBranchProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.slug);
  const isActive = activeTagSlugs.includes(node.slug);

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
          isActive
            ? 'bg-orange-500/20 text-orange-300'
            : 'hover:bg-gray-800 text-gray-300'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={() => toggleExpand(node.slug)}
            className="w-3 text-gray-600 hover:text-gray-400 shrink-0 text-[10px]"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="w-3 shrink-0" />
        )}

        {/* Tag name — click to search */}
        <button
          onClick={(e: React.MouseEvent) => {
            const multi = e.metaKey || e.ctrlKey;
            if (!multi && isActive && activeTagSlugs.length === 1) {
              clearSearch();
            } else {
              toggleTagFilter(node.slug, multi);
            }
          }}
          className="flex-1 text-left truncate"
        >
          {node.displayName}
        </button>

        {/* Count */}
        <span className={`font-medium tabular-nums shrink-0 ${isActive ? 'text-orange-400' : 'text-gray-500'}`}>
          {node.totalCount}
        </span>
      </div>

      {/* Children (collapsible) */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagTreeBranch
              key={child.slug}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              toggleExpand={toggleExpand}
              activeTagSlugs={activeTagSlugs}
              toggleTagFilter={toggleTagFilter}
              clearSearch={clearSearch}
            />
          ))}
        </div>
      )}
    </div>
  );
}
